import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { InvoiceBillingAddressDescription, InvoiceBillingAddressSubtitle, InvoiceBillingAddressWrapper } from './index.styled'
import { useDispatch, useSelector } from 'react-redux'
import Button from '../../../../buttons/Button'
import CustomModal from '../../../../modal/CustomModal'
import MainBillingAddressForm from './main'
import { formatAddress, removeEmptyFields } from '../../../../../../helper'
import { setIsInvoiceChangeEmail, setIsInvoiceCorrection, setSelectedInvoice, setShowInvoiceBillingAddressModal, setShowInvoiceResendEmailInfoModal } from '../../../../../../redux/action/invoice'
import { convert2InvoiceCustomer, convert2InvoiceRecipient } from './helper'
import { correctInvoiceThunk, updateInvoiceCustomerThunk } from '../../../../../../redux/thunk/invoice'
import { selectIsInvoiceCorrection } from '../../../../../../redux/selector/invoice'

const EVENT_Z_INDEX = 82

const defaultValues = {
	phoneNumber: null, customerNumber: null, name: null, companyName: null, address: null, streetNumber: null,
	street: null, city: null, postalCode: null, customerAddress: null,
}

const getSubmitButton = (t, isInvoiceCorrection, isInvoiceChangeEmail, isCorrecting) => {
	if (isInvoiceCorrection) {
		return { name: 'correct-invoice', icon: 'save', text: t('invoices.modal.optionsModal.buttons.correctInvoice'), disabled: isCorrecting }
	}
	if (isInvoiceChangeEmail) {
		return { name: 'send-email', icon: 'send-email', text: t('buttons.controls.send_invoice') }
	}
	return { name: 'save', icon: 'save', text: t('buttons.controls.save') }
}

const InvoiceBillingAddressModal = React.memo(() => {
	const { t } = useTranslation()
	const { handleSubmit, register, formState: { errors }, reset, setValue, watch, setFocus, clearErrors } = useForm({ defaultValues })
	const dispatch = useDispatch()
	const showBillingAddressModal = useSelector(store => store.invoice.modal.showBillingAddress)
	const selectedInvoice = useSelector(store => store.invoice.selectedInvoice)
	const isInvoiceChangeEmail = useSelector(store => store.invoice.isInvoiceChangeEmail)
	const isInvoiceCorrection = useSelector(selectIsInvoiceCorrection)
	const [isCorrecting, setIsCorrecting] = useState(false)
	const handleReset = useCallback((values) => {
		reset({ ...defaultValues, ...values })
	}, [reset])

	useEffect(() => {
		if (selectedInvoice) {
			const { street, streetNumber, postalCode, city, name, companyName } = selectedInvoice.customer.billingAddress
			handleReset({
				customerNumber: selectedInvoice.customer.customerNumber,
				phoneNumber: selectedInvoice?.customer?.phoneNumber,
				streetNumber: streetNumber || null,
				companyName: companyName || null,
				address: formatAddress(street, streetNumber, postalCode, city),
				name: name || null,
				email: selectedInvoice.customer.email,
				customerAddress: {
					city: city || null,
					street: street || null,
					house: streetNumber || null,
					zipCode: postalCode || null,
				},
			})
		}
	}, [selectedInvoice]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => () => {
		dispatch(setIsInvoiceCorrection(false))
	}, [dispatch])

	const handleOpenResendEmailInfoModal = useCallback((data) => {
		const customerData = convert2InvoiceCustomer(data)
		dispatch(updateInvoiceCustomerThunk(selectedInvoice.id, customerData))
		dispatch(setShowInvoiceBillingAddressModal(false))
		dispatch(setIsInvoiceChangeEmail(false))
		if (!isInvoiceChangeEmail) {
			dispatch(setShowInvoiceResendEmailInfoModal(true))
		}
	}, [dispatch, selectedInvoice.id, isInvoiceChangeEmail])

	const handleClose = useCallback(() => {
		dispatch(setShowInvoiceBillingAddressModal(false))
		handleReset(defaultValues)
		dispatch(setSelectedInvoice(null))
		dispatch(setIsInvoiceChangeEmail(false))
		dispatch(setIsInvoiceCorrection(false))
	}, [dispatch, handleReset])

	const handleCorrectInvoice = useCallback(async (data) => {
		setIsCorrecting(true)
		const correctedInvoice = await dispatch(correctInvoiceThunk(selectedInvoice.id, convert2InvoiceRecipient(selectedInvoice.customer.customerNumber, data)))
		setIsCorrecting(false)
		if (correctedInvoice) {
			handleClose()
		}
	}, [dispatch, selectedInvoice, handleClose])

	const onSubmit = useCallback(async (data) => {
		let dataValue = {
			...data,
			address: formatAddress(data.customerAddress.street, data.customerAddress.house, data.customerAddress.zipCode, data.customerAddress.city),
			city: data.customerAddress.city,
			street: data.customerAddress.street,
			postalCode: data.customerAddress.zipCode,
			streetNumber: data.customerAddress.house,
		}
		dataValue = removeEmptyFields(dataValue)
		if (isInvoiceCorrection) {
			await handleCorrectInvoice(dataValue)
		} else {
			handleOpenResendEmailInfoModal(dataValue)
		}
	}, [isInvoiceCorrection, handleCorrectInvoice, handleOpenResendEmailInfoModal])

	const submitButton = getSubmitButton(t, isInvoiceCorrection, isInvoiceChangeEmail, isCorrecting)

	return (
		<CustomModal isOpen={showBillingAddressModal}
								 close={handleClose}
								 title={t(isInvoiceCorrection ? 'invoices.modal.billingAddress.correctionTitle' : 'invoices.modal.billingAddress.title')}
								 size="x-small"
								 id="invoice-billing-address-modal"
								 zIndex={EVENT_Z_INDEX}
								 disableBackButton={true}
								 button={
									 <>
										 <Button name="cancel" icon="clear" text={t('buttons.controls.cancel_1')}
														 keyButton="F3" zIndex={EVENT_Z_INDEX}
														 onClick={handleClose}
														 className="back" />
										 <Button {...submitButton} color="green" keyButton="F2" zIndex={EVENT_Z_INDEX}
														 onClick={handleSubmit(onSubmit)} />
									 </>
								 }>
			<InvoiceBillingAddressWrapper>
				<InvoiceBillingAddressSubtitle>{t('invoices.modal.billingAddress.subtitle')}</InvoiceBillingAddressSubtitle>
				<MainBillingAddressForm {...{ handleSubmit, onSubmit, register, errors, setValue, setFocus, watch, zIndex: EVENT_Z_INDEX, clearErrors }} />
				<InvoiceBillingAddressDescription>{'*' + t('invoices.modal.billingAddress.description')}</InvoiceBillingAddressDescription>
			</InvoiceBillingAddressWrapper>
		</CustomModal>
	)
})

InvoiceBillingAddressModal.displayName = 'InvoiceBillingAddressModal'
export default InvoiceBillingAddressModal
